// Økonomi: kroner, indtægt ved årsskifte, tidligt kald og pakker.

import type { LevelDef, PackId, Perks, SimEvent, State, ThreatKind } from './types';
import { SEASON_TICKS, TICK_HZ, YEAR_TICKS } from './types';
import { getLevel } from '../content/levels';
import { cashFactorOf, difficulty, levelRampOf } from './difficulty';
import { isForeignLine, slotsFree, upgradeCost } from './grid';
import { shuffled } from './rng';

/**
 * Playtest runde 3: 40 kr gav 0-4 kr i kassen de første år. 45 kr giver ét træk mere fra start på
 * Lærling og Vagthavende; de svære niveauer skalerer tilbage til 40 og 32 kr (startBudgetFactor).
 */
export const START_BUDGET = 45;
export const DATACENTER_INCOME = 8;
/**
 * Grundtilskud pr. år. Lærling og Vagthavende: forrest i banen, 12, 9, 6 og derefter 3 kr. Playtest
 * runde 3: kassen sultede de første år og flød over i finalen; indtægten pr. MW vokser med nettet, så
 * tilskuddet skal bære starten, ikke slutningen. Systemansvarlig og Orkan beholder de faste 5 kr: dér
 * er den stramme kasse en del af udfordringen (balance: gennemsnitsbotten vandt ellers alt).
 */
export const YEAR_GRANTS_GENTLE: readonly number[] = [12, 9, 6, 3];
export const YEAR_GRANTS_FLAT: readonly number[] = [5];
export function yearGrant(state: Pick<State, 'year' | 'difficulty'>): number {
  const g = difficulty(state.difficulty).frontGrant ? YEAR_GRANTS_GENTLE : YEAR_GRANTS_FLAT;
  return g[Math.min(Math.max(0, state.year), g.length - 1)] as number;
}
/** Kroner pr. 100 MW, byerne vokser med ved årsskiftet (betales oven i indtægten). */
export const GROWTH_GRANT_PER_100MW = 5;
/** Kroner pr. 100 MW leveret i årsgennemsnit. */
export const INCOME_PER_100MW = 3;
/** Fees som producenterne betaler ved første tilslutning. */
export const DEFAULT_FEE: Record<string, number> = { wind: 4, solar: 3, offshore: 10, plant: 0, datacenter: 0 };
/** Årsskiftet vælger selv efter 12 s (den pakke, reservebotten rangerer højest), så det ikke bliver et hul. */
export const YEAREND_AUTO_TICKS = 120;
/**
 * Sammen vælger I gaven: 30 s, så I når at tale om den (IGN runde 2: "gaven blev valgt for os, mens
 * vi stadig snakkede"). Rummet kan ikke vente for evigt på en, der er gået fra telefonen.
 */
export const YEAREND_AUTO_TICKS_COOP = 300;

/** Ticks før årsskiftet vælger gaven selv. */
export function yearendAutoTicks(state: Pick<State, 'coop'>): number {
  return state.coop ? YEAREND_AUTO_TICKS_COOP : YEAREND_AUTO_TICKS;
}
/**
 * Næste år tidligt: 10 % plus 1 % pr. sparet sekund, højst 20 %. Playtest runde 3: 30-60 % gjorde
 * knappen til et pligttryk hvert år. Den er en spoleknap for et færdigt år, ikke en pengemaskine, og
 * dødtiden løses af at kunderne kommer før (CALM_PULL_TICKS i step.ts).
 */
export const EARLY_BASE_PCT = 10;
/**
 * Året kan først kaldes, når vinteren har stået på i 3 s: vintertoppen er årets prøve, og den må
 * ikke kunne springes over med bonus oveni (playtest runde 2).
 */
export const EARLY_FROM_TICK = 3 * SEASON_TICKS + 3 * TICK_HZ;
export const EARLY_PCT_PER_S = 1;
export const EARLY_MAX_PCT = 20;

/**
 * Begge gaver (spor "sværhed"): ved årsskiftet kan man tage den anden gave med for en pris, der
 * stiger år for år: 10, 14, 18, 22 og 26 kr. Det er kassens sene dræn. Balance: kassen hobede sig op
 * til 60-137 kr, fordi intet synligt var værd at købe sidst i banen; en ekstra gave er det.
 */
export const BOTH_BASE_KR = 10;
export const BOTH_STEP_KR = 4;
/** Prisen for at tage begge gaver ved årsskiftet efter året `year` (0 = første år). */
export function bothPrice(year: number): number {
  return BOTH_BASE_KR + BOTH_STEP_KR * Math.max(0, year);
}

export function emptyPerks(): Perks {
  return { freeAir: 0, freeDouble: 0, freeBattery: 0, freeFlywheel: 0, freeStation: 0, cableDiscountPct: 0, foreignBoostPct: 0, townMeeting: false };
}

export function startBudget(factor: number): number {
  return Math.round(START_BUDGET * factor);
}

/** Årets indtægt før bonus: grundtilskud (yearGrant), 3 kr pr. 100 MW leveret i snit, 8 kr pr. datacenter, 1 kr pr. 200 MW eksport. */
export function yearIncome(state: State): number {
  const ys = state.yearStats;
  const ticks = Math.max(1, ys.ticks);
  const avgServed = ys.servedSum / ticks;
  const avgExport = ys.exportSum / ticks;
  let dc = 0;
  for (const n of state.nodes) {
    if (n.kind === 'datacenter' && n.active && !n.phantom && n.city && n.city.servedMw > 0) dc++;
  }
  const raw = yearGrant(state) + Math.round((INCOME_PER_100MW * avgServed) / 100) + DATACENTER_INCOME * dc + Math.round(avgExport / 200);
  return Math.round(raw * cashFactor(state));
}

/** Kassens stramning på denne bane og sværhed (1 på bane 1-2). */
export function cashFactor(state: Pick<State, 'difficulty' | 'levelId'>): number {
  return cashFactorOf(difficulty(state.difficulty), getLevel(state.levelId).number);
}

/** Ticks mellem to dryp af løbende indtægt (20 pr. år). */
export const DRIP_TICKS = 30;
export const DRIPS_PER_YEAR = YEAR_TICKS / DRIP_TICKS;
/** Byer, et dryp nævner som afsender. */
export const DRIP_FROM = 3;

/** Andelen af årets indtægt, der kommer løbende (resten ved årsskiftet). */
export function dripShare(state: State): number {
  const d = difficulty(state.difficulty);
  return Math.min(1, d.advanceShare / levelRampOf(d, getLevel(state.levelId).number));
}

/**
 * Løbende indtægt: hvert 3. sekund en tyvendedel af årets andel af det, året ser ud til at give, så
 * kassen tikker op hele året og ikke kommer i klumper ved årstiderne. Resten under 1 kr gemmes til
 * næste dryp. Årsskiftet trækker det udbetalte fra. Ikke i finaleåret, som heller ikke har noget
 * årsskifte, og ikke på selve årsskiftet. Returnerer beløbet.
 */
export function incomeDrip(state: State, events: SimEvent[]): number {
  if (state.finale) return 0;
  const yt = state.clockTick % YEAR_TICKS;
  if (yt === 0 || yt % DRIP_TICKS !== 0) return 0;
  const dueMilli = Math.round((yearIncome(state) * dripShare(state) * 1000) / DRIPS_PER_YEAR);
  const total = state.cashCarryMilli + dueMilli;
  const amount = Math.floor(total / 1000);
  state.cashCarryMilli = total - amount * 1000;
  if (amount < 1) return 0;
  state.budget += amount;
  state.advancePaid += amount;
  const from = state.nodes
    .filter((n) => n.city && n.kind === 'city' && n.active && !n.phantom && !n.city.out && n.city.servedMw > 0)
    .sort((a, b) => (b.city?.servedMw ?? 0) - (a.city?.servedMw ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .slice(0, DRIP_FROM)
    .map((n) => n.id);
  events.push({ t: 'income_tick', tick: state.tick, amount, from });
  return amount;
}

/** Bonus i procent for at kalde året nu. */
export function earlyBonusPct(state: State): number {
  if (state.clockTick % YEAR_TICKS < EARLY_FROM_TICK) return 0;
  const left = YEAR_TICKS - (state.clockTick % YEAR_TICKS);
  const secs = Math.floor(left / TICK_HZ);
  if (secs <= 0) return 0;
  return Math.min(EARLY_MAX_PCT, EARLY_BASE_PCT + EARLY_PCT_PER_S * secs);
}

/** Hændelser, der bryder ledninger, så ekstra montørhold har noget at lave. */
const BREAK_KINDS: readonly ThreatKind[] = ['storm', 'digger', 'anchor', 'icing', 'cable_half'];
/** Kunder i puljen, før en kabelrabat er værd at vælge. */
export const CABLE_DISCOUNT_MIN_POOL = 2;
/** Har nettet så mange montørhold, er to mere et dødt valg (playtest runde 3: 5/5 holdt stille). */
export const CREWS_ENOUGH = 4;

/** Kan pakken bruges lige nu? En død pakke er et dødt valg, så tilbuddet trækker kun blandt brugbare. */
export function packUseful(state: State, level: LevelDef, pack: PackId): boolean {
  const has = (m: LevelDef['mechanics'][number]): boolean => level.mechanics.includes(m);
  const upgradable = (double: boolean): boolean =>
    state.lines.some(
      (l) => l.status === 'ok' && !l.halfDown && upgradeCost(state, l) !== null && (!double || (l.tier === 2 && l.medium !== 'sea' && l.medium !== 'hvdc')),
    );
  switch (pack) {
    case 'two_crews': {
      // Flere brud end vogne: holdene er flaskehalsen. Ét brud med en vogn på vej er det ikke
      // (playtest runde 3: tilbudt efter en storm, hvor reparationerne aldrig ventede).
      const vans = state.crews.filter((c) => c.kind === 'van').length;
      if (state.lines.filter((l) => l.status === 'broken' || l.halfDown).length > vans) return true;
      return vans < CREWS_ENOUGH && level.eventMenu.some((k) => BREAK_KINDS.includes(k));
    }
    case 'cable_discount': {
      // Havvind, der venter på et søkabel, eller nok kunder til at et jordkabel kan komme på tale.
      // Ikke "godt til Thor", når Thor allerede er bygget (playtest runde 3).
      const offshore = state.nodes.some(
        (n) => n.kind === 'offshore' && !n.phantom && (state.pool.includes(n.id) || (n.active && !state.lines.some((l) => l.a === n.id || l.b === n.id))),
      );
      return (has('sea') && offshore) || (has('cable') && state.pool.length >= CABLE_DISCOUNT_MIN_POOL);
    }
    case 'free_station':
      // En station er værd at få, når en node er ved at løbe tør for pladser.
      return has('station') && state.nodes.some((n) => n.active && !n.phantom && n.kind !== 'foreign' && n.station !== 1 && slotsFree(state, n) <= 1);
    case 'free_air3':
      return has('air') && upgradable(false);
    case 'free_double':
      return has('double') && upgradable(true);
    case 'town_meeting':
      return level.eventMenu.includes('protest') || state.threats.some((t) => t.kind === 'protest');
    case 'foreign_plus50':
      return state.lines.some((l) => isForeignLine(state, l));
    case 'big_battery':
    case 'flywheel':
      return (
        has(pack === 'flywheel' ? 'flywheel' : 'battery') &&
        state.nodes.some((n) => n.kind === 'city' && n.active && !n.phantom && !!n.city && n.city.servedMw > 0)
      );
  }
}

/** Reservebottens foretrukne rækkefølge (først er bedst), når begge pakker kan bruges. */
export const PACK_PREFERENCE: readonly PackId[] = [
  'two_crews',
  'free_station',
  'free_air3',
  'free_double',
  'cable_discount',
  'town_meeting',
  'big_battery',
  'flywheel',
  'foreign_plus50',
];

/**
 * Hvor meget reservebotten vil have pakken: brugbare først, så efter PACK_PREFERENCE. Trinene er 12,
 * så bottens seedede støj (±5) aldrig bytter om på to pakker, og automatvalget altid tager den
 * pakke, botten ville tage.
 */
export function packValue(state: State, level: LevelDef, pack: PackId): number {
  const idx = PACK_PREFERENCE.indexOf(pack);
  return (packUseful(state, level, pack) ? 120 : 0) + 12 * (PACK_PREFERENCE.length - (idx < 0 ? PACK_PREFERENCE.length : idx));
}

/** Kroner, botten og rådgiveren altid vil have tilbage efter begge gaver (før finalen). */
export const BOTH_RESERVE_KR = 8;

/**
 * Er begge gaver købet værd? Begge skal kunne bruges, og kassen skal have råd med en reserve til
 * året, der kommer. Før finalen er der intet år at spare op til, så dér må hele kassen gå. Mangler en
 * by strøm, går pengene til nettet først: en gave tænder ikke en by, en ny ledning gør.
 */
export function bothWorth(state: State, level: LevelDef): boolean {
  if (state.phase !== 'yearend' || state.packOffer.length < 2) return false;
  if (!state.packOffer.every((p) => packUseful(state, level, p))) return false;
  for (const n of state.nodes) {
    if (n.active && !n.phantom && n.city && n.city.servedMw < n.city.demandMw) return false;
  }
  const reserve = state.year + 1 >= level.years ? 0 : BOTH_RESERVE_KR;
  return state.budget - bothPrice(state.year) >= reserve;
}

/** Indekset i tilbuddet, automatvalget tager (højeste packValue, første ved lighed). */
export function autoPackIndex(state: State, level: LevelDef): number {
  let best = 0;
  for (let i = 1; i < state.packOffer.length; i++) {
    if (packValue(state, level, state.packOffer[i] as PackId) > packValue(state, level, state.packOffer[best] as PackId)) best = i;
  }
  return best;
}

/**
 * Årets pakketilbud: to forskellige pakker fra banens liste, trukket med `rng.packs` blandt dem,
 * der kan bruges lige nu. Er der færre end to brugbare, fyldes op med de andre.
 */
/**
 * Ligger gaven allerede ubrugt på hylden? IGN runde 2: "Ny station" blev tilbudt igen, mens en
 * gratis station stadig ventede. En gave, man allerede har, er et dødt valg.
 */
export function packHeld(state: State, pack: PackId): boolean {
  const p = state.perks;
  switch (pack) {
    case 'free_station':
      return (p.freeStation ?? 0) > 0;
    case 'free_double':
      return p.freeDouble > 0;
    case 'free_air3':
      return p.freeAir > 0;
    case 'big_battery':
      return p.freeBattery > 0;
    case 'flywheel':
      return p.freeFlywheel > 0;
    case 'town_meeting':
      return Boolean(p.townMeeting);
    case 'cable_discount':
      return p.cableDiscountPct > 0;
    default:
      return false;
  }
}

/** Forbrugsgaver: brugt op, kan de tilbydes igen, når hele kataloget er brugt (Spor: tillid). */
export const REFILLABLE: readonly PackId[] = ['free_air3', 'free_double', 'free_station', 'big_battery', 'flywheel'];

export function packOffer(state: State, level: LevelDef): PackId[] {
  const own: PackId[] = [];
  for (const pair of level.packs) for (const p of pair) if (!own.includes(p)) own.push(p);
  if (own.length === 0) return [];
  // Spor: tillid (Pocket Gamer runde 4: "Søkabelrabat i 2032 og 2034, Borgermøde i 2031 og 2034 ...
  // Dobbelt 400 kV i 3 af 5 år"). Gaverne trækkes som fra en pose: en gave, spilleren har taget eller
  // stadig har på hylden, kommer aldrig igen i banen, og en gave, der har været tilbudt, kommer først
  // igen, når posen er tom. Banens egne gaver først; slipper de op, fyldes der op med andre gaver fra
  // kataloget, som kan bruges på banen lige nu.
  const taken = state.packsTaken ?? [];
  const seen = state.packsSeen ?? [];
  const extra = PACK_PREFERENCE.filter((p) => !own.includes(p) && packUseful(state, level, p));
  const open = [...own, ...extra].filter((p) => !taken.includes(p) && !packHeld(state, p));
  const useful = open.filter((p) => packUseful(state, level, p));
  const rank = (p: PackId): number =>
    Number((state.lastOffer ?? []).includes(p)) * 4 + Number(seen.includes(p)) * 2 + Number(!own.includes(p));
  // Blandet først (seedet), så ordnet stabilt efter rang: blandt lige gode gaver bestemmer terningen.
  const offer = shuffled(state.rng.packs, useful).sort((a, b) => rank(a) - rank(b)).slice(0, 2);
  if (offer.length < 2) {
    // Færre end to brugbare: fyld op med en gave, der ikke er taget, før en, der er.
    const rest = open.filter((p) => !offer.includes(p));
    offer.push(...shuffled(state.rng.packs, rest).sort((a, b) => rank(a) - rank(b)).slice(0, 2 - offer.length));
  }
  if (offer.length < 2) {
    // Sidste udvej (kun når hele kataloget er brugt): en forbrugsgave, der er brugt op, kan fyldes op
    // igen; den, der ikke var med sidste år, og ellers den, der blev taget længst tid siden. En varig
    // gave (hold, borgermøde, rabat, udlandsforbindelse) kommer aldrig igen. Er der ikke noget, er
    // tilbuddet bare kortere.
    const again = taken
      .filter((p) => REFILLABLE.includes(p) && !offer.includes(p) && !packHeld(state, p) && packUseful(state, level, p))
      .sort((a, b) => Number((state.lastOffer ?? []).includes(a)) - Number((state.lastOffer ?? []).includes(b)));
    offer.push(...again.slice(0, 2 - offer.length));
  }
  return offer;
}

/** Giv pakkens effekt. Montørhold oprettes af kalderen via `hireCrew`. */
export function applyPack(state: State, pack: PackId, hireCrew: () => void, events: SimEvent[], auto: boolean): void {
  const p = state.perks;
  switch (pack) {
    case 'free_air3':
      p.freeAir += 3;
      break;
    case 'free_double':
      p.freeDouble += 1;
      break;
    case 'big_battery':
      p.freeBattery += 1;
      break;
    case 'two_crews':
      hireCrew();
      hireCrew();
      break;
    case 'foreign_plus50':
      p.foreignBoostPct += 50;
      break;
    case 'flywheel':
      p.freeFlywheel += 1;
      break;
    case 'town_meeting':
      p.townMeeting = true;
      break;
    case 'cable_discount':
      p.cableDiscountPct = 30;
      break;
    case 'free_station':
      p.freeStation = (p.freeStation ?? 0) + 1;
      break;
  }
  events.push({ t: 'pack_picked', tick: state.tick, pack, auto });
}
